import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';
import { ROLES } from '../utils/roles';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    // Check if user is logged in on mount
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const loadUser = async () => {
    try {
      // Set token in axios headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      const response = await authAPI.getMe();
      const userData = response?.data || response;
      setCurrentUser(userData);
    } catch (error) {
      console.error('Error loading user:', error);
      // If token is invalid, clear it
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      const authData = response?.data || response;
      const { token: newToken, employee } = authData || {};

      if (!newToken) {
        throw new Error('Invalid login response');
      }
      
      // Save token to localStorage
      localStorage.setItem('token', newToken);
      setToken(newToken);
      
      // Set token in axios headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      setCurrentUser(employee);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error?.message || error?.error || 'Login failed'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      const authData = response?.data || response;
      const { token: newToken, employee } = authData || {};

      if (!newToken) {
        throw new Error('Invalid registration response');
      }
      
      // Save token to localStorage
      localStorage.setItem('token', newToken);
      setToken(newToken);
      
      // Set token in axios headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      setCurrentUser(employee || authData);
      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Registration failed'
      };
    }
  };

  const logout = () => {
    // Clear token from localStorage
    localStorage.removeItem('token');
    setToken(null);
    
    // Remove token from axios headers
    delete axios.defaults.headers.common['Authorization'];
    
    setCurrentUser(null);
  };

  const hasPermission = (permission) => {
    if (!currentUser) return false;

    // Check leads permission restriction: only Sales, HR, and Admin departments/roles
    if (typeof permission === 'string' && permission.includes('lead')) {
      const roleValue = currentUser.role;
      const normalizedRole = typeof roleValue === 'string' ? roleValue.toLowerCase() : '';
      const customRoleName = (currentUser.customRole?.name || '').toLowerCase();
      const department = (currentUser.department || '').toLowerCase();
      
      const isAdminOrHR = 
        normalizedRole === 'admin' || 
        normalizedRole === 'super_admin' || 
        normalizedRole === 'hr' ||
        customRoleName.includes('admin') || 
        customRoleName.includes('super admin') || 
        customRoleName.includes('hr') ||
        department === 'hr';
        
      const isSales = department === 'sales';

      if (!isAdminOrHR && !isSales) {
        return false;
      }
    }

    const roleValue = currentUser.role;
    const normalizedRole = typeof roleValue === 'string' ? roleValue.toLowerCase() : '';

    if (normalizedRole === 'admin' || normalizedRole === 'super_admin') {
      return true;
    }

    // If role is an object with permissions array, use it directly
    let rolePermissions = Array.isArray(roleValue?.permissions) ? roleValue.permissions : [];

    // If role is a plain string and no permissions found on object, look up in ROLES definitions
    if (rolePermissions.length === 0 && typeof roleValue === 'string') {
      const roleDef = Object.values(ROLES).find(
        r => r.id === normalizedRole || r.name.toLowerCase() === normalizedRole
      );
      if (roleDef) {
        rolePermissions = roleDef.permissions || [];
      }
    }

    const customRolePermissions = Array.isArray(currentUser.customRole?.permissions)
      ? currentUser.customRole.permissions
      : [];

    const userPermissions = [...rolePermissions, ...customRolePermissions];

    if (userPermissions.includes('all')) return true;

    return userPermissions.includes(permission);
  };

  const isPrivileged = currentUser ? (() => {
    const roleValue = currentUser.role;
    const normalizedRole = typeof roleValue === 'string' ? roleValue.toLowerCase() : '';
    const roleName = (currentUser.customRole?.name || '').toLowerCase();
    
    // Check permissions
    let rolePermissions = Array.isArray(roleValue?.permissions) ? roleValue.permissions : [];
    if (rolePermissions.length === 0 && typeof roleValue === 'string') {
      const roleDef = Object.values(ROLES).find(
        r => r.id === normalizedRole || r.name.toLowerCase() === normalizedRole
      );
      if (roleDef) {
        rolePermissions = roleDef.permissions || [];
      }
    }

    const customRolePermissions = Array.isArray(currentUser.customRole?.permissions)
      ? currentUser.customRole.permissions
      : [];

    const userPermissions = [...rolePermissions, ...customRolePermissions];

    if (userPermissions.includes('all')) return true;
    if (roleName.includes('super admin')) return true;
    if (roleName.includes('hr')) return true;
    if (roleName.includes('admin')) return true;
    if (normalizedRole === 'admin' || normalizedRole === 'super_admin' || normalizedRole === 'hr' || normalizedRole === 'hr_manager') return true;

    return false;
  })() : false;

  const value = {
    currentUser,
    login,
    register,
    logout,
    hasPermission,
    isPrivileged,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
